import { gsap } from 'gsap/all';
import Papa from 'papaparse';
import Logger from '../lib/logger';
import { CA_MAP, US_MAP } from './maps';

class InteractiveMap extends HTMLElement {
  #NAME = 'InteractiveMap';
  #abortController;
  #country;
  #isSVG = false;
  #svg;
  #svgViewHeight;
  #image;
  #hoverMask;
  #groups;
  #mapDiv;
  #infoDiv;
  #infoArea;
  #infoStat;
  #infoStatName;
  #data;
  #displayData;
  #time;
  #metric;

  constructor() {
    super();
    Logger.info(`${this.#NAME} > constructor`);
    this.#mapDiv = this.querySelector('[data-map]');
    this.#infoDiv = this.querySelector('[data-info]');
    this.#infoArea = this.#infoDiv.querySelector('[data-info-area]');
    this.#infoStat = this.#infoDiv.querySelector('[data-info-stat]');
    this.#infoStatName = this.#infoDiv.querySelector('[data-info-stat-name]');
  }

  connectedCallback() {
    Logger.info(`${this.#NAME} > connectedCallback > ${this.#country}`);
    this.#abortController =  new AbortController();
    if (this.#data) this.init();
  }

  disconnectedCallback() {
    Logger.info(`${this.#NAME} > disconnectedCallback > ${this.#country}`);
    this.#abortController.abort();
    this.#abortController = null;
  }

  static get observedAttributes() {
    return ['country', 'data-url', 'data-time', 'data-metric', 'data-select-results'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    Logger.info(`${this.#NAME} > attributeChangedCallback > name: ${name} > newValue: ${newValue}`);
    //
    switch (name) {
      case 'country':
        this.#country = newValue;
        //
        if (this.#country === 'United States') {
          this.#mapDiv.innerHTML = US_MAP;
          this.#isSVG = true;
        } else if (this.#country === 'Canada') {
          this.#mapDiv.innerHTML = CA_MAP;
          this.#isSVG = true;
        }
        break;

      case 'data-url':
        if (newValue && newValue.endsWith('.csv')) {
          Papa.parse(newValue, {
            download: true,
            header: true,
            skipEmptyLines: true,
            complete: (results, file) => {
              Logger.info(`${this.#NAME} > Parsing CSV complete`, results, file);
              this.#data = {};
              if (results.data) {
                results.data.forEach(obj => {
                  this.#data[obj.geoname] = { ...obj };
                  delete this.#data[obj.geoname].geoname;
                });
                console.log(this.#data);
                this.init();
              }
            },
            error: (err, file) => {
              Logger.info(`${this.#NAME} > Parsing CSV error`, err, file);
            }
          });
        }
        break;

      case 'data-time':
        this.#time = newValue;
        break;

      case 'data-metric':
        this.#metric = newValue;
        break;

      case 'data-select-results':
        const resultsObjArr = [];
        const resultsArr = newValue.split(',').filter(v => v.length);
        resultsArr.forEach(v => {
          const values = v.split('|');
          const obj = {
            time: values[0],
            metric: values[1],
            data: values[2],
            subtext: values[3],
            showArrow: values[4]
          };
          resultsObjArr.push(obj);
        });
        this.#displayData = resultsObjArr;
        break;
    
      default:
        break;
    }
  }

  init() {
    const { signal } = this.#abortController;
    //
    if (this.#isSVG) {
      this.#svg = this.querySelector('svg');
      this.#svgViewHeight = this.#svg.viewBox.baseVal.height;
      this.#groups = Array.from(this.#svg.querySelectorAll('g[id]'));
      this.colorize();
      this.#groups.forEach(group => {
        gsap.set(group, {transformOrigin: 'center'});
        group.addEventListener('mouseover', () => this.onOver(group), { signal });
        group.addEventListener('mouseout', () => this.onOut(group), { signal });
      });
      this.addEventListener('mouseout', () => {
        this.#groups.forEach(g => {
          this.onOut(g);
          g.querySelectorAll('[class^="st0"]').forEach(el => {
            el.classList.add(el.dataset.class);
           });
        });
        gsap.to(this.#infoDiv, { opacity: 0, duration: 0.3, ease: 'power2.in' });
      }, { signal });
    } else {
      // images
      this.#image = this.querySelector('img');
      this.#hoverMask = document.createElement('div');
      this.#hoverMask.className = 'st0-xx hover-mask';
      this.#hoverMask.style = `--src: url(${this.#image.src})`;
      this.#mapDiv.id = 'Entire Country';
      this.#mapDiv.appendChild(this.#hoverMask);
      this.#groups = [this.#mapDiv];
      this.colorize();
      this.#image.id = 'Entire Country';
      this.#image.addEventListener('mouseover', () => this.onOver(this.#image), { signal });
      this.#image.addEventListener('mouseout', () => {
        gsap.to(this.#infoDiv, { opacity: 0, duration: 0.3, ease: 'power2.in' });
      }, { signal });
    }
  }

  colorize() {
    this.#groups.forEach(g => {
      const id = g.id.replaceAll('_', ' ');
      const data = this.#data[id];
      const md = data[this.dataset.colorizeKey];
      const els = g.querySelectorAll('[class^="st0"]');
      //
      switch (this.#country) {
        case 'United States':
          if (md <= 3.96) {
            els.forEach(el => {
              el.dataset.class = 'color-1';
              el.classList.add('color-1');
            });
          } else if (md <= 5.37) {
            els.forEach(el => {
              el.dataset.class = 'color-2';
              el.classList.add('color-2');
            });
          }  else if (md <= 6.78) {
            els.forEach(el => {
              el.dataset.class = 'color-3';
              el.classList.add('color-3');
            });
          } else if (md <= 8.61) {
            els.forEach(el => {
              el.dataset.class = 'color-4';
              el.classList.add('color-4');
            });
          } else {
            els.forEach(el => {
              el.dataset.class = 'color-5';
              el.classList.add('color-5');
            });
          }
          break;

        case 'Canada':
          if (md <= 1.15) {
            els.forEach(el => {
              el.dataset.class = 'color-1';
              el.classList.add('color-1');
            });
          } else if (md <= 1.19) {
            els.forEach(el => {
              el.dataset.class = 'color-2';
              el.classList.add('color-2');
            });
          }  else if (md <= 1.46) {
            els.forEach(el => {
              el.dataset.class = 'color-3';
              el.classList.add('color-3');
            });
          } else if (md <= 1.98) {
            els.forEach(el => {
              el.dataset.class = 'color-4';
              el.classList.add('color-4');
            });
          } else {
            els.forEach(el => {
              el.dataset.class = 'color-5';
              el.classList.add('color-5');
            });
          }
          break;

        case 'United Kingdom':
          if (md <= 0.93) {
            els.forEach(el => {
              el.dataset.class = 'color-1';
              el.classList.add('color-1');
            });
          } else if (md <= 1.26) {
            els.forEach(el => {
              el.dataset.class = 'color-2';
              el.classList.add('color-2');
            });
          }  else if (md <= 1.62) {
            els.forEach(el => {
              el.dataset.class = 'color-3';
              el.classList.add('color-3');
            });
          } else if (md <= 2.12) {
            els.forEach(el => {
              el.dataset.class = 'color-4';
              el.classList.add('color-4');
            });
          } else {
            els.forEach(el => {
              el.dataset.class = 'color-5';
              el.classList.add('color-5');
            });
          }
          break;

        case 'Australia':
          if (md <= 0.63) {
            els.forEach(el => {
              el.dataset.class = 'color-1';
              el.classList.add('color-1');
            });
          } else if (md <= 0.86) {
            els.forEach(el => {
              el.dataset.class = 'color-2';
              el.classList.add('color-2');
            });
          }  else if (md <= 1.16) {
            els.forEach(el => {
              el.dataset.class = 'color-3';
              el.classList.add('color-3');
            });
          } else if (md <= 1.45) {
            els.forEach(el => {
              el.dataset.class = 'color-4';
              el.classList.add('color-4');
            });
          } else {
            els.forEach(el => {
              el.dataset.class = 'color-5';
              el.classList.add('color-5');
            });
          }
          break;
      
        default:
          break;
      }
    })
  }

  onOver(group) {
    Logger.info(`${this.#NAME} > onOver > ${group.id}`);
    const id = group.id.replaceAll('_', ' ');
    this.setInfoContent(id);
    //
    if (this.#isSVG) {
      const bbox = group.lastElementChild.getBBox();
      const scale = this.#svg.clientHeight / this.#svgViewHeight;
      const dif = this.#mapDiv.clientWidth - this.#svg.clientWidth
      //
      this.#groups.filter(g => g !== group).forEach(g => {
        this.onOut(g);
        g.querySelectorAll('[class^="st0"]').forEach(el => {
          el.classList.remove(el.dataset.class);
        });
      });
      this.#svg.appendChild(group);
      //
      gsap.to(group, { scale: 1.13, ease: 'power2.out',  duration: 0.4 });
      gsap.set(this.#infoDiv, { x: (dif/2) + (bbox.x + (bbox.width/2))* scale, y: ((bbox.y - 16) * scale) - 8 });
    } else {
      // image
      gsap.set(this.#infoDiv, { x: this.#mapDiv.clientWidth/2, y: this.#mapDiv.clientHeight/2 - 32 });
    }
    gsap.to(this.#infoDiv, { opacity: 1, duration: 0.4, ease: 'power2.out' });
    
  }

  onOut(group) {
    if (group.getAttribute('transform') !== 'matrix(1,0,0,1,0,0)') {
      /* Logger.info(`${this.#NAME} > onOut > ${group.id}`); */
      gsap.to(group, { scale: 1, ease: 'power2.in', duration: 0.3 });
    }
  }

  setInfoContent(id) {
    const data = this.#data[id];
    const displayData = this.#displayData.find(obj => obj.time === this.#time && obj.metric === this.#metric);
    this.#infoArea.textContent = id;
    this.#infoStat.textContent = data[displayData.data];
    this.#infoStatName.textContent = displayData.subtext;
    this.#infoStat.dataset.down = displayData.showArrow === '1' ? data[displayData.data].startsWith('-') : '';
  }

  getImage() {
    Logger.info(`${this.#NAME} > getImage > ${this.#country}`);
    let href, fileName;
    if (this.#isSVG) {
      href = 'data:image/svg+xml;base64,' + window.btoa(this.#svg.outerHTML);
      fileName = `${this.#country}-map.svg`;
    } else {
      const srcArr = this.#image.src.split('.');
      href = this.#image.src;
      fileName = `${this.#country}-map.${srcArr[srcArr.length-1]}`;
    }
    return { href, fileName }
  }

}

customElements.define('interactive-map', InteractiveMap);